import { useMemo, type ReactNode } from "react";
import { ArrowUpRight, Inbox, ListTodo, TrendingUp, Trophy, Users, Wallet } from "lucide-react";
import { BarChart, DonutChart } from "./charts";
import { Avatar, ProductTag, StatusBadge } from "./widgets";
import { TaskRow } from "./taskWidgets";
import { formatCurrency, formatDayMonth, productLabel, relativeTime, taskBucket } from "./format";
import {
  PRODUCT_COLORS,
  PRODUCT_ORDER,
  STATUS_COLORS,
  STATUS_ORDER,
  type AdminLead,
  type LeadStatus,
  type LeadTask
} from "./types";

const DAY = 86_400_000;

function startOfDay(date: Date) {
  date.setHours(0, 0, 0, 0);
  return date;
}

export function Dashboard({
  leads,
  tasks,
  onOpen,
  onGoLeads,
  onGoTasks,
  onToggleTask
}: {
  leads: AdminLead[];
  tasks: LeadTask[];
  onOpen: (id: string) => void;
  onGoLeads: () => void;
  onGoTasks: () => void;
  onToggleTask: (task: LeadTask, done: boolean) => void;
}) {
  const leadMap = useMemo(() => new Map(leads.map((lead) => [lead.id, lead])), [leads]);
  const attentionTasks = useMemo(
    () =>
      tasks
        .filter((task) => {
          const bucket = taskBucket(task.dueAt, task.done);
          return bucket === "overdue" || bucket === "today";
        })
        .sort((a, b) => a.dueAt.localeCompare(b.dueAt))
        .slice(0, 6),
    [tasks]
  );
  const stats = useMemo(() => {
    const byStatus = Object.fromEntries(STATUS_ORDER.map((s) => [s, 0])) as Record<LeadStatus, number>;
    const byProduct: Record<string, number> = {};
    const dayCounts = new Map<string, number>();
    let pipelineValue = 0;
    let newThisWeek = 0;
    let unassigned = 0;
    const weekAgo = Date.now() - 7 * DAY;

    for (const lead of leads) {
      byStatus[lead.status] += 1;
      byProduct[lead.product] = (byProduct[lead.product] ?? 0) + 1;
      if (lead.status === "new" || lead.status === "contacted" || lead.status === "quoted") {
        pipelineValue += lead.budget ?? 0;
      }
      const created = new Date(lead.createdAt).getTime();
      if (created >= weekAgo) newThisWeek += 1;
      if (!lead.owner && lead.status !== "won" && lead.status !== "lost") unassigned += 1;
      const dayKey = startOfDay(new Date(lead.createdAt)).toISOString().slice(0, 10);
      dayCounts.set(dayKey, (dayCounts.get(dayKey) ?? 0) + 1);
    }

    const open = byStatus.new + byStatus.contacted + byStatus.quoted;
    const closed = byStatus.won + byStatus.lost;
    const conversion = closed > 0 ? Math.round((byStatus.won / closed) * 100) : 0;

    const today = startOfDay(new Date()).getTime();
    const days = Array.from({ length: 14 }, (_, idx) => {
      const date = new Date(today - (13 - idx) * DAY);
      const key = date.toISOString().slice(0, 10);
      return { label: formatDayMonth(date), value: dayCounts.get(key) ?? 0 };
    });

    const productSegments = PRODUCT_ORDER.filter((p) => byProduct[p]).map((p) => ({
      label: productLabel(p, true),
      value: byProduct[p],
      color: PRODUCT_COLORS[p]
    }));

    const recent = [...leads].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 6);

    return {
      total: leads.length,
      byStatus,
      open,
      conversion,
      newThisWeek,
      pipelineValue,
      unassigned,
      days,
      productSegments,
      recent
    };
  }, [leads]);

  const funnelMax = Math.max(1, ...STATUS_ORDER.map((s) => stats.byStatus[s]));

  return (
    <div className="crm-view">
      <div className="crm-kpis">
        <KpiCard
          icon={<Users size={18} />}
          tone="navy"
          label="Wszystkie leady"
          value={stats.total}
          sub={`+${stats.newThisWeek} w tym tygodniu`}
        />
        <KpiCard
          icon={<Inbox size={18} />}
          tone="blue"
          accent
          label="Nowe do kontaktu"
          value={stats.byStatus.new}
          sub={stats.unassigned ? `${stats.unassigned} nieprzypisanych` : "wszystkie przypisane"}
        />
        <KpiCard
          icon={<TrendingUp size={18} />}
          tone="violet"
          label="W toku"
          value={stats.byStatus.contacted + stats.byStatus.quoted}
          sub="kontakt + oferta"
        />
        <KpiCard
          icon={<Trophy size={18} />}
          tone="green"
          label="Sprzedane"
          value={stats.byStatus.won}
          sub={`konwersja ${stats.conversion}%`}
        />
        <KpiCard
          icon={<Wallet size={18} />}
          tone="amber"
          label="Potencjał mies."
          value={formatCurrency(stats.pipelineValue)}
          sub="otwarte zgłoszenia"
        />
      </div>

      <div className="crm-grid-2">
        <section className="crm-card">
          <header className="crm-card-head">
            <h2>Nowe leady — ostatnie 14 dni</h2>
          </header>
          <BarChart data={stats.days} />
        </section>
        <section className="crm-card">
          <header className="crm-card-head">
            <h2>Wg produktu</h2>
          </header>
          {stats.productSegments.length ? (
            <DonutChart segments={stats.productSegments} />
          ) : (
            <p className="crm-empty">Brak danych.</p>
          )}
        </section>
      </div>

      <div className="crm-grid-2">
        <section className="crm-card">
          <header className="crm-card-head">
            <h2>
              <ListTodo size={16} aria-hidden="true" /> Zadania — wymagają uwagi
            </h2>
            <button type="button" className="crm-link" onClick={onGoTasks}>
              Wszystkie <ArrowUpRight size={14} aria-hidden="true" />
            </button>
          </header>
          {attentionTasks.length === 0 ? (
            <p className="crm-empty">Brak pilnych zadań na dziś. 🎉</p>
          ) : (
            <div className="crm-task-list">
              {attentionTasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  lead={task.leadId ? leadMap.get(task.leadId) ?? null : null}
                  onToggle={onToggleTask}
                  onOpenLead={onOpen}
                />
              ))}
            </div>
          )}
        </section>

        <section className="crm-card">
          <header className="crm-card-head">
            <h2>Lejek sprzedaży</h2>
          </header>
          <ul className="crm-funnel">
            {STATUS_ORDER.map((status) => (
              <li key={status}>
                <span className="crm-funnel-label">
                  <StatusBadge status={status} />
                </span>
                <span className="crm-funnel-bar">
                  <span
                    style={{
                      width: `${(stats.byStatus[status] / funnelMax) * 100}%`,
                      background: STATUS_COLORS[status]
                    }}
                  />
                </span>
                <strong>{stats.byStatus[status]}</strong>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="crm-card">
        <header className="crm-card-head">
          <h2>Ostatnie zgłoszenia</h2>
          <button type="button" className="crm-link" onClick={onGoLeads}>
            Wszystkie <ArrowUpRight size={14} aria-hidden="true" />
          </button>
        </header>
        <ul className="crm-recent">
          {stats.recent.map((lead) => (
            <li
              key={lead.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(lead.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpen(lead.id);
                }
              }}
            >
              <Avatar name={lead.name} size={38} />
              <div className="crm-recent-main">
                <strong>{lead.name}</strong>
                <span>
                  <ProductTag product={lead.product} /> · {lead.city || "—"}
                </span>
              </div>
              <div className="crm-recent-meta">
                <StatusBadge status={lead.status} />
                <span>{relativeTime(lead.createdAt)}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function KpiCard({
  icon,
  tone,
  label,
  value,
  sub,
  accent = false
}: {
  icon: ReactNode;
  tone: string;
  label: string;
  value: ReactNode;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div className={`crm-kpi${accent ? " crm-kpi--accent" : ""}`}>
      <span className={`crm-kpi-icon ${tone}`}>{icon}</span>
      <span className="crm-kpi-label">{label}</span>
      <strong className="crm-kpi-value">{value}</strong>
      <span className="crm-kpi-sub">{sub}</span>
    </div>
  );
}
